/* eslint-disable import/no-default-export */
import { dismissAnnouncement, State } from 'app/gamma/src/modules';
import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { AnnouncementResponse } from 'app/gamma/src/types';

import { Popover, PopoverProps } from 'app/gamma/src/components/popover';
import moment from 'moment';
import { getLocaleAnnouncements } from 'app/gamma/src/selectors/announcements';
import {
  isUnread,
  setTacoShow,
} from 'app/gamma/src/util/application-storage/announcements';

import { Announcement } from './announcement';
import styles from './announcement.less';

import { forNamespace, forTemplate } from 'app/common/lib/util/i18n';

import Icon, { IconName } from 'app/common/components/controls/icon';
import { Noun, Verb, trackUe } from '@trello/analytics';

const viewTitle = forNamespace('view title');
const format = forTemplate('header_announcements');
const socialUrls = forNamespace('social');

export const POPOVER_NAME = Symbol('ROO_ANNOUNCEMENT');

interface AnnouncementsProps {
  announcements: AnnouncementResponse[];
  loading: boolean;
}

interface DispatchProps {
  readonly dismissAnnouncement: (id: string) => void;
}

interface AllProps
  extends AnnouncementsProps,
    DispatchProps,
    Pick<PopoverProps, 'onClose' | 'target'> {}

class Announcements extends React.Component<AllProps> {
  componentDidMount() {
    if (this.props.announcements) {
      this.markAnnouncementsRead(this.props.announcements);
    }
  }

  componentDidUpdate(prevProps: AllProps) {
    if (prevProps.announcements.length !== this.props.announcements.length) {
      this.markAnnouncementsRead(this.props.announcements);
    }
  }

  onTrackAnalytics = (socialUrl: string) => {
    trackUe({
      category: Noun.HEADER,
      verb: Verb.OPENS,
      directObj: Noun.LANDING_TAB,
      context: {
        landingtab: socialUrl,
      },
    });
  };

  markAnnouncementsRead = (announcements: AnnouncementResponse[]) => {
    this.props.announcements.forEach((announcement) => {
      const { id } = announcement;
      if (isUnread(id)) {
        this.props.dismissAnnouncement(id);
      }
    });
    setTacoShow(
      moment()
        .add(1, 'hours')
        .valueOf(),
    );
  };

  render() {
    const { announcements, target } = this.props;

    let campaign = '';
    if (announcements.length > 0) {
      const urlPart = /\/\/^https?:\/\/(?:[-a-z]+\.)?blog\.trello\.com\/([^/]+)\/\//.exec(
        announcements[0].url,
      );
      campaign = urlPart ? urlPart[1] : 'none';
    }

    if (/\?/g.test(campaign)) {
      campaign = campaign.replace(/\?/gi, '&');
    }

    let blogQuery = `?utm_campaign=${campaign}`;

    if (!/utm_source/.test(campaign)) {
      blogQuery += '&utm_source=direct';
    }

    if (!/utm_medium/.test(campaign)) {
      blogQuery += '&utm_medium=inapp';
    }

    return (
      <Popover
        name={POPOVER_NAME}
        target={target}
        title={viewTitle('announcements')}
        width={304}
        contentClassName={styles.popoverContent}
      >
        {() => (
          <div>
            {announcements.map((announcement) => (
              <Announcement key={announcement.id} {...announcement} />
            ))}
            <div className={styles['announcement-elsewhere']}>
              <p className={styles.quiet} style={{ margin: '0 4px 5px' }}>
                {format('for-more-updates-check-out-ellipsis')}
              </p>

              <a
                className={styles['announcement-elsewhere-blog']}
                target="_blank"
                rel="noopener noreferrer"
                href={`${socialUrls('blog')}${blogQuery}`}
                onClick={() => this.onTrackAnalytics(socialUrls('blog'))}
              >
                <Icon
                  className={styles['announcement-elsewhere-blog-icon']}
                  name={IconName.Board}
                  large
                />
                <span className={styles.underline}>
                  {format('trello-blog')}
                </span>
              </a>

              <a
                className={styles['announcement-elsewhere-social']}
                target="_blank"
                rel="noopener noreferrer"
                href={socialUrls('twitter')}
                onClick={() => this.onTrackAnalytics(socialUrls('twitter'))}
              >
                <Icon name={IconName.Twitter} large />
              </a>
              <a
                className={styles['announcement-elsewhere-social']}
                target="_blank"
                rel="noopener noreferrer"
                href={socialUrls('facebook')}
                onClick={() => this.onTrackAnalytics(socialUrls('facebook'))}
              >
                <Icon name={IconName.Facebook} large />
              </a>
              <a
                className={styles['announcement-elsewhere-social']}
                target="_blank"
                rel="noopener noreferrer"
                href={socialUrls('instagram')}
                onClick={() => this.onTrackAnalytics(socialUrls('instagram'))}
              >
                <Icon name={IconName.Instagram} large />
              </a>
            </div>
          </div>
        )}
      </Popover>
    );
  }
}

function mapStateToProps(state: State) {
  return {
    loading: state.models.announcements.loading,
    announcements: getLocaleAnnouncements(state),
  };
}

function mapDispatchToProps(dispatch: Dispatch<State>) {
  return {
    dismissAnnouncement(id: string) {
      dispatch(dismissAnnouncement(id));
    },
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Announcements);
